/**
 * Axios instance for Market Mavericks API.
 * - baseURL from env (REACT_APP_API_URL) or fallback to local backend
 * - Attaches Bearer token from localStorage to every request
 * - Use this for all API calls; import in api/* modules and pages
 */

import axios from 'axios';

/**
 * API origin: REACT_APP_API_URL, or local backend in dev, or same origin in production.
 * If REACT_APP_API_URL ends with /api, it is stripped — paths already include /api/... and would double otherwise.
 */
export const getApiBaseUrl = () => {
  const raw = process.env.REACT_APP_API_URL;
  if (raw !== undefined && String(raw).trim() !== '') {
    let base = String(raw).trim().replace(/\/+$/, '');
    if (base.toLowerCase().endsWith('/api')) {
      base = base.slice(0, -4).replace(/\/+$/, '');
    }
    return base;
  }
  if (process.env.NODE_ENV === 'development') {
    return 'http://localhost:5001';
  }
  return '';
};

/** Absolute URL for API paths (e.g. uploads, upload endpoints). */
export const apiUrl = (path) => {
  if (!path) return "";
  if (path.startsWith("http://") || path.startsWith("https://")) {
    return path;
  }
  const p = path.startsWith("/") ? path : `/${path}`;
  const base = getApiBaseUrl();
  if (!base) return p;
  return `${base.replace(/\/$/, '')}${p}`;
};

const BASE_URL = getApiBaseUrl();

export const apiClient = axios.create({
  baseURL: BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

const TOKEN_KEY = 'market_mavericks_token';

export const getStoredToken = () =>
  localStorage.getItem(TOKEN_KEY) || sessionStorage.getItem(TOKEN_KEY);

/**
 * Store auth token. When rememberMe is true, use localStorage (persists across sessions).
 * When false, use sessionStorage (cleared when tab/browser closes).
 */
export const setStoredToken = (token, rememberMe = true) => {
  if (token) {
    if (rememberMe) {
      localStorage.setItem(TOKEN_KEY, token);
      sessionStorage.removeItem(TOKEN_KEY);
    } else {
      sessionStorage.setItem(TOKEN_KEY, token);
      localStorage.removeItem(TOKEN_KEY);
    }
  } else {
    localStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(TOKEN_KEY);
  }
};

apiClient.interceptors.request.use((config) => {
  const token = getStoredToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    const isAuthError = status === 401 || status === 403;
    const isNetworkError = !error.response;

    if (isAuthError || isNetworkError) {
      const token = getStoredToken();
      // Only trigger logout if there's an active session
      if (token) {
        setStoredToken(null);
        localStorage.removeItem('market_mavericks_auth');
        sessionStorage.removeItem('market_mavericks_auth');

        // Prevent infinite redirect loops if already on a public/auth page
        const publicPaths = ['/login', '/register', '/forgot-password', '/reset-password'];
        const isPublicPath = publicPaths.some((path) =>
          window.location.pathname.startsWith(path)
        );

        if (!isPublicPath && window.location.pathname !== '/') {
          window.location.href = '/';
        }
      }
    }
    return Promise.reject(error);
  }
);

export default apiClient;
